import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChildren
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {MatSnackBar} from '@angular/material/snack-bar';
import {FastSearchService} from '@app/core/api/fast-search.service';
import {DeviceService} from '@app/core/services/device.service';
import {RouterService} from '@app/core/services/router.service';
import {PayloadUtil} from '@app/core/util/payload.util';
import {RightUtil} from '@app/core/util/right.util';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {FastSearchKunde, FastSearchPolizze, FastSearchResponse, FastSearchSchaden, RightsConst} from '@pezi/entities';
import {debounceTime, switchMap} from 'rxjs/operators';
import { TextSmallDirective } from '../../core/directive/text-small.directive';
import { NgClass, NgIf, NgFor, CurrencyPipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { FormatNumberPipe } from '@app/shared/pipe/format-number-pipe';

@UntilDestroy()
@Component({
  selector:        'app-fast-search',
  templateUrl:     './fast-search.component.html',
  styleUrls:       ['./fast-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone:      true,
  imports:         [
    TextSmallDirective,
    NgClass,
    NgIf,
    ReactiveFormsModule,
    MatIcon,
    NgFor,
    CurrencyPipe,
    FormatNumberPipe
  ]
})
export class FastSearchComponent implements OnInit, AfterViewInit {

  @Input()
  public showSearch = false;
  @Output()
  activeChanged = new EventEmitter<boolean>();

  public currentSearch = '';


  public fastSearch = new FormGroup({
    search: new FormControl(
      {
        value: null,
        disabled: false
      }
    )
  });
  public searchResults: FastSearchResponse;
  public searchTime = 0;
  public searchString: string;
  public selectedIndex = -1;
  public maxIndex = 0;
  public fastSearchError: string;
  public searching = false;
  public showResultCategory = {
    all: false,
    person: true,
    polizze: false,
    schaden: false
  };

  private shiftPressed = false;

  @ViewChildren('searchinput') searchInput: QueryList<ElementRef>;
  @ViewChildren('innerSearchResultList') searchResultList: QueryList<ElementRef>;

  constructor(
    public deviceService: DeviceService, private cd: ChangeDetectorRef,
    private rightUtil: RightUtil, private routerService: RouterService,
    private fastSearchService: FastSearchService, private snackbar: MatSnackBar) {
    this.deviceService
      .getDeviceSubject()
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.cd.markForCheck();
      });
  }

  @HostListener('document:click', ['$event'])
  clickout(event: any) {
    /*
    if (!this.deviceService.isHandset()) {
      this.showSearch = (event && (event.target.classList.contains('fast-search') || event.target.classList.contains('search-ele') || (event.target.classList.contains('material-icons') && event.target.parentElement.classList.contains('search-ele'))));
    }
    */
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent) {
    if (this.showSearch && ['Tab', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
      event.preventDefault();
    }
    if (this.showSearch && event.key === 'Shift') { //shift
      this.shiftPressed = true;
    }
  }

  @HostListener('keyup', ['$event'])
  onKeypress(event: KeyboardEvent) {
    if (this.showSearch && event.key === 'Shift') { //shift
      this.shiftPressed = false;
    }

    if (this.searchResultList?.length > 0) {
      this.changeDropdownIndex(event);
    }
    if (this.showSearch && event.key === 'Enter') { // enter
      this.openSearchItemsByCategory();
    }

    if (this.showSearch && ['Tab', 'ArrowLeft', 'ArrowRight'].includes(event.key)) { //tab, arrow left, arrow right
      this.changeCategory(event)
    }
    this.cd.markForCheck();
  }


  ngOnInit() {
    this.fastSearch.controls.search.valueChanges.pipe(untilDestroyed(this),
      debounceTime(150),
      switchMap(value => {
        this.searching = true;
        this.searchString = value.toLowerCase()
          .replace(/[^a-zA-Z0-9äöüÄÖÜß. ]/g, '').replace(/\./g, '');
        return this.fastSearchService.getSuche(this.searchString);
      })
    ).subscribe(
      result => {
        this.fastSearchError = '';
        this.searching = false;
        if (PayloadUtil.checkPayload(result)) {
          this.searchResults = result.data;
          this.selectedIndex = -1;
          this.maxIndex = this.searchResults?.person?.length + this.searchResults?.polizze?.length + this.searchResults?.schaden?.length;
          this.setSearchCategory();
          this.cd.markForCheck();
        } else {
          this.fastSearchError = result.message.map(v => v.text).join(' / ');
        }
      }
    );
  }

  ngAfterViewInit() {
    if (this.searchInput) {
      this.searchInput.changes.pipe(untilDestroyed(this)).subscribe(() => {
          if (this.searchInput.length > 0) {
            this.searchInput.first.nativeElement.focus();
          }
        }
      )
    }
  }

  private changeCategory(event: KeyboardEvent): void {
    const tabDirectionFw = (!this.shiftPressed && event.key === 'Tab') || event.key === 'ArrowRight'; // shift not down and tab pressed or arrow right pressed -> move forward
    if (this.showResultCategory.all) {
      this.setSearchCategory((!tabDirectionFw ? 'schaden' : 'person'));
    } else if (this.showResultCategory.person) {
      this.setSearchCategory((!tabDirectionFw ? 'all' : 'polizze'));
    } else if (this.showResultCategory.polizze) {
      this.setSearchCategory((!tabDirectionFw ? 'person' : 'schaden'));
    } else if (this.showResultCategory.schaden) {
      this.setSearchCategory((!tabDirectionFw ? 'polizze' : 'all'));
    }
  }

  private openSearchItemsByCategory(): void {
    if (this.showResultCategory.person) {
      this.openSearchItemPerson(this.searchResults.person[this.selectedIndex]);
    }
    if (this.showResultCategory.polizze) {
      this.openSearchItemPolizze(this.searchResults.polizze[this.selectedIndex]);
    }
    if (this.showResultCategory.schaden) {
      this.openSearchItemSchaden(this.searchResults.schaden[this.selectedIndex]);
    }
    if (this.showResultCategory.all) {
      if (this.selectedIndex + 1 <= this.searchResults.person.length) {
        this.openSearchItemPerson(this.searchResults.person[this.selectedIndex]);
      }
      if (this.selectedIndex + 1 >= this.searchResults.person.length && this.selectedIndex + 1 <= (this.searchResults.person.length * this.searchResults.polizze.length)) {
        this.openSearchItemPolizze(this.searchResults.polizze[(this.selectedIndex - this.searchResults.person.length)]);
      }
      if (this.selectedIndex + 1 >= this.searchResults.polizze.length && this.selectedIndex + 1 <= (this.searchResults.polizze.length * this.searchResults.schaden.length)) {
        this.openSearchItemSchaden(this.searchResults.schaden[(this.selectedIndex - (this.searchResults.person.length + this.searchResults.polizze.length))]);
      }
    }
    this.showSearch = false;
  }

  private changeDropdownIndex(event: KeyboardEvent): void {
    const searchItems = this.searchResultList.first.nativeElement.children.length;
    if (this.showSearch && event.key === 'ArrowDown' && (this.selectedIndex + 1 < searchItems)) { // arrow down
      this.selectedIndex++;
    }
    if (this.showSearch && event.key === 'ArrowUp' && this.selectedIndex >= 0) { // arrow up
      this.selectedIndex--;
    }
    this.setDropDownPosition();
  }

  private setDropDownPosition() {
    if (this.searchResultList?.first?.nativeElement?.children[this.selectedIndex]) {
      const ele = (this.searchResultList.first.nativeElement.children[this.selectedIndex].offsetTop + this.searchResultList.first.nativeElement.children[this.selectedIndex].offsetHeight) - this.searchResultList.first.nativeElement.children[this.selectedIndex].offsetHeight;
      this.searchResultList.first.nativeElement.scroll({
        top: (ele > this.searchResultList.first.nativeElement.offsetHeight ? this.searchResultList.first.nativeElement.children[this.selectedIndex].offsetTop - (this.searchResultList.first.nativeElement.offsetHeight) : 0),
        left: 0,
        behavior: 'smooth'
      });
    } else {
      if (this.selectedIndex === -1 && this.searchResultList?.first) {
        this.searchResultList.first.nativeElement.scroll({
          top: 0,
          left: 0,
          behavior: 'smooth'
        });
      }
    }
  }

  public setSearchCategory(category?: string): void {
    this.selectedIndex = -1;
    this.setDropDownPosition();
    if (category) {
      this.showResultCategory.all = (category === 'all');
      this.showResultCategory.person = (category === 'person');
      this.showResultCategory.polizze = (category === 'polizze');
      this.showResultCategory.schaden = (category === 'schaden');
    }
    if (!this.showResultCategory.all) {
      if (this.showResultCategory.person && this.searchResults.person.length === 0) {
        this.setSearchCategory('polizze');
        return;
      }
      if (this.showResultCategory.polizze && this.searchResults.polizze.length === 0) {
        this.setSearchCategory('schaden');
        return;
      }
      if (this.showResultCategory.schaden && this.searchResults.schaden.length === 0) {
        if (this.searchResults.person.length + this.searchResults.polizze.length + this.searchResults.schaden.length === 0) {
          this.setSearchCategory('all');
        } else {
          this.setSearchCategory('person');
        }
      }
    }

  }

  public openSearchItem(index: number) {
    if (index < this.searchResults?.person?.length) {
      this.openSearchItemPerson(this.searchResults.person[index]);
    } else if (index < this.searchResults?.person?.length + this.searchResults?.polizze?.length) {
      this.openSearchItemPolizze(this.searchResults.polizze[index - this.searchResults?.person?.length]);
    } else if (index < this.searchResults?.person?.length + this.searchResults?.polizze?.length + this.searchResults?.schaden?.length) {
      this.openSearchItemSchaden(this.searchResults.schaden[index - this.searchResults?.person?.length - this.searchResults?.polizze?.length]);
    }
  }

  public openSearchItemPerson(kunde: FastSearchKunde) {
    if (kunde) {
      if (this.rightUtil.hasRight(RightsConst.PZ_PERSON)) {
        this.routerService.navigatePersonDetail(kunde.person.personId);
      } else if (this.rightUtil.hasRight(RightsConst.PZ_MAKLER_PERSON)) {
        this.routerService.navigateMaklerPersonDetail(kunde.person.personId);
      } else {
        this.snackbar.open('Für diese Aktion besitzen Sie keine Berechtigung.');
      }
    }
    this.closeSearch();
  }

  public openSearchItemPolizze(polizze: FastSearchPolizze) {
    if (polizze) {
      if (this.rightUtil.hasRight(RightsConst.PZ_POLIZZE)) {
        this.routerService.navigatePoliceDetail(polizze.polizzeId);
      } else if (this.rightUtil.hasRight(RightsConst.PZ_MAKLER_POLIZZEN)) {
        this.routerService.navigateMaklerPolizzeDetail(polizze.polizzeId);
      } else {
        this.snackbar.open('Für diese Aktion besitzen Sie keine Berechtigung.');
      }
    }
    this.closeSearch();
  }

  public openSearchItemSchaden(schaden: FastSearchSchaden) {
    if (schaden) {
      if (this.rightUtil.hasRight(RightsConst.PZ_SCHADEN)) {
        this.routerService.navigateSchadenDetail(schaden.schadenId);
      } else if (this.rightUtil.hasRight(RightsConst.PZ_MAKLER_SCHADEN)) {
        this.routerService.navigateMaklerSchadenDetail(schaden.schadenId);
      } else {
        this.snackbar.open('Für diese Aktion(en) besitzen Sie keine Berechtigung.');
      }
    }
    this.closeSearch();
  }

  public onKeyDownSearch(val: KeyboardEvent) {
    this.showSearch = true;
    const filteredText = val.key
      .replace(/[^a-zA-Z0-9äöüÄÖÜß. ]/g, '');
    if (filteredText.length < 1) {
      val.preventDefault();
    }
  }

  public scrollToElement(element: string) {
    const el = document.getElementById(element);
    el.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest'
    });
  }

  public closeSearch() {
    this.selectedIndex = -1;
    this.showSearch = false;
    this.activeChanged.emit(this.showSearch);
  }
}
